import {
  isRouteType,
  isWebpageRoute,
  pickPrimaryRoute,
  type RouteType,
} from "../../shared/stepConfig";
import type { AppBranch, AppRoute, PageRow } from "./PagesList";

export type PageCardRouteFilter = {
  typeFilter: RouteType | null;
  favFilter: boolean;
  routeFavoriteSet: ReadonlySet<string>;
};

export type PageCardEntry = {
  row: PageRow;
  branch: AppBranch;
  route: AppRoute;
  // All of the workflow's webpage routes when they're collapsed into this one
  // card; the count badge shows the total and lists them.
  webpageRoutes?: AppRoute[];
};

// The single source of truth for which routes render as cards, shared by the
// grid and the header count so they can't disagree (the type pill and the
// Favorites tab both apply here). `routeFavoriteSet` is keyed
// `${workflowId}:${stepDir}`.
export function visiblePageCardRoutes(
  rows: PageRow[],
  { typeFilter, favFilter, routeFavoriteSet }: PageCardRouteFilter
): PageCardEntry[] {
  const out: PageCardEntry[] = [];
  for (const row of rows) {
    const matches: PageCardEntry[] = [];
    for (const branch of row.workflow.branches ?? []) {
      if (branch.name !== "main") continue;
      for (const route of branch.headCommit?.routes ?? []) {
        if (!isRouteType(route.routeType)) continue;
        if (typeFilter && route.routeType !== typeFilter) continue;
        if (
          favFilter &&
          !routeFavoriteSet.has(`${row.workflow.id}:${route.stepDir}`)
        )
          continue;
        matches.push({ row, branch, route });
      }
    }
    const webpageRoutes = matches
      .filter((m) => isWebpageRoute(m.route.routeType))
      .map((m) => m.route);
    const primaryWebpage = pickPrimaryRoute(webpageRoutes);
    for (const m of matches) {
      if (!isWebpageRoute(m.route.routeType)) {
        out.push(m);
      } else if (m.route === primaryWebpage) {
        out.push(webpageRoutes.length > 1 ? { ...m, webpageRoutes } : m);
      }
    }
  }
  return out;
}

export function countPageCards(
  rows: PageRow[],
  filter: PageCardRouteFilter
): number {
  return visiblePageCardRoutes(rows, filter).length;
}
